// The auth pair lives outside React so that the api layer can read it without every
// page having to thread `context.authData` through its controller on each call.
// It is seeded from localStorage at import time, which means a request fired from a
// mount effect already sees the right credentials on the very first render.

import type { AuthData } from "types/api";

const STORAGE_KEYS = {
  domain: "d",
  token: "t",
};

const EMPTY_AUTH: AuthData = Object.freeze({ domain: "", token: "" });

const readStorage = (): AuthData => {
  try {
    const domain = localStorage.getItem(STORAGE_KEYS.domain);
    const token = localStorage.getItem(STORAGE_KEYS.token);

    return domain && token ? { domain: domain, token: token } : EMPTY_AUTH;
  } catch (error) {
    // Private mode or a blocked storage must not take the whole app down.
    return EMPTY_AUTH;
  }
};

let snapshot = readStorage();

export const getAuth = (): AuthData => snapshot;

export const hasAuth = (): boolean => Boolean(snapshot.domain && snapshot.token);

export const saveAuth = ({ domain, token }: AuthData): AuthData => {
  snapshot = { domain: domain, token: token };

  try {
    localStorage.setItem(STORAGE_KEYS.domain, domain);
    localStorage.setItem(STORAGE_KEYS.token, token);
  } catch (error) {
    /* keep the in-memory pair even when it cannot be persisted */
  }

  return snapshot;
};

export const clearAuth = (): AuthData => {
  snapshot = EMPTY_AUTH;

  try {
    localStorage.removeItem(STORAGE_KEYS.domain);
    localStorage.removeItem(STORAGE_KEYS.token);
  } catch (error) {
    /* nothing to clean up */
  }

  return snapshot;
};
